import { MODAL_TYPE } from 'config/Constants';
import { format, parse } from 'date-fns';
import { useStore } from 'hooks/useStore';
import { useEffect } from 'react';
import {toast} from 'react-toastify';

export const useScheduleAttainment = () => {
  // Store and state
  const { ScheduleAttainmentStore, GlobalModalStore, roomStore, shiftTimingStore, JobPasteStore } =
    useStore();

  const visibility = GlobalModalStore.getCurrentInstance(
    MODAL_TYPE.PPS_JOB_PASTE_SCHEDULE_ATTAINMENT_MODAL
  );
  const environment = import.meta.env.VITE_ENVIRONMENT;

  const roomList = roomStore.rooms.map(room => room.room_long);

  const roomCodeMap = roomStore.rooms.reduce((acc, curr) => {
    return {
      ...acc,
      [curr.room_long]: curr.room_short
    };
  }, {}) as { [key: string]: string };

  const shifts = shiftTimingStore.shiftTimings.map(shift => shift.shift_name);

  const defaultShiftMap = shiftTimingStore.shiftTimings.reduce((acc, curr) => {
    // Parse the 24-hour format times to Date objects
    // Assuming times are in format like "14:15:00" or "06:15:00"
    const startTime = parse(curr.start_time, 'HH:mm:ss', new Date());
    const endTime = parse(curr.end_time, 'HH:mm:ss', new Date());

    // Format the times to 12-hour format with AM/PM
    const formattedStartTime = format(startTime, 'h:mm a');
    const formattedEndTime = format(endTime, 'h:mm a');

    return {
      ...acc,
      [curr.shift_name]: {
        start: formattedStartTime,
        end: formattedEndTime
      }
    };
  }, {});

  const onHide = () =>
    GlobalModalStore.toggleModal(MODAL_TYPE.PPS_JOB_PASTE_SCHEDULE_ATTAINMENT_MODAL);

  // Initialize data when modal is opened
  useEffect(() => {
    const initializeData = async () => {
      try {
        // Initialize the form data structure first
        ScheduleAttainmentStore.initializeFormData(
          Object.values(roomCodeMap),
          shifts,
          defaultShiftMap
        );

        // Fetch the data
        await ScheduleAttainmentStore.fetchScheduleAttainment();
      } catch (error) {
        if(environment === 'development'){
          console.error('Error initializing schedule attainment data:', error);
        }
        toast.error('Error initializing schedule attainment data');
      }
    };

    initializeData();
  }, [visibility]); // Re-run when visibility changes

  // Event handlers for UI interactions
  const handleDateChange = async (date: Date) => {
    ScheduleAttainmentStore.setDate(date);

    // Re-initialize with the new date
    ScheduleAttainmentStore.initializeFormData(Object.values(roomCodeMap), shifts, defaultShiftMap);

    await ScheduleAttainmentStore.fetchScheduleAttainment();
  };

  const handleRoomStatusChange = (roomCode: string, shift: string, value: string) => {
    ScheduleAttainmentStore.updateFormField(
      roomCode,
      shift,
      'isRoomRunning',
      value,
      defaultShiftMap
    );
  };

  const handleStartTimeChange = (roomCode: string, shift: string, value: string) => {
    ScheduleAttainmentStore.updateFormField(
      roomCode,
      shift,
      'startTimeOverride',
      value,
      defaultShiftMap
    );
  };

  const handleEndTimeChange = (roomCode: string, shift: string, value: string) => {
    ScheduleAttainmentStore.updateFormField(
      roomCode,
      shift,
      'endTimeOverride',
      value,
      defaultShiftMap
    );
  };

  const handleSave = async () => {
    await ScheduleAttainmentStore.saveScheduleOverrides();
    if (!ScheduleAttainmentStore.isLoading && !ScheduleAttainmentStore.isSaving) {
      onHide();
    }
  };

  return {
    isLoading:
      ScheduleAttainmentStore.isLoading || roomStore.isLoading || shiftTimingStore.isLoading,
    isSaving: ScheduleAttainmentStore.isSaving,
    date: ScheduleAttainmentStore.date,
    formData: ScheduleAttainmentStore.formData,
    handleDateChange,
    handleRoomStatusChange,
    handleStartTimeChange,
    handleEndTimeChange,
    handleSave,
    roomList,
    roomCodeMap,
    shifts,
    visibility,
    onHide,
    JobPasteStore,
    isPastView: ScheduleAttainmentStore.isPastView
  };
};
